import { z } from 'zod';

/** Create-time / on-demand VCS access verification (not analysis). */
export const SourceAccessVerifyResultSchema = z.object({
  repoId: z.string().uuid(),
  verified: z.boolean(),
  accessVerifiedAt: z.string().nullable(),
  error: z.string().max(500).nullable(),
  code: z
    .enum(['ok', 'no_integration', 'invalid_credentials', 'invalid_repository', 'unreachable', 'not_found'])
    .optional(),
});
export type SourceAccessVerifyResult = z.infer<typeof SourceAccessVerifyResultSchema>;

export const SourceAccessVerifyResponseSchema = SourceAccessVerifyResultSchema;
export type SourceAccessVerifyResponse = SourceAccessVerifyResult;

/** Local folder path probe via Execution Agent (not a full scan). */
export const WorkspacePathVerifyResultSchema = z.object({
  workspaceId: z.string().uuid(),
  verified: z.boolean(),
  pathVerifiedAt: z.string().nullable(),
  resolvedPath: z.string().nullable().optional(),
  error: z.string().max(500).nullable(),
  code: z
    .enum(['ok', 'agent_unavailable', 'path_not_found', 'not_directory', 'timeout', 'not_found', 'queue_full'])
    .optional(),
});
export type WorkspacePathVerifyResult = z.infer<typeof WorkspacePathVerifyResultSchema>;

export const WorkspacePathVerifyResponseSchema = WorkspacePathVerifyResultSchema;
export type WorkspacePathVerifyResponse = WorkspacePathVerifyResult;

/** Analyze Application orchestrator — explicit user action only. */
export const KnowledgeAnalyzeStepStatusSchema = z.enum(['pending', 'running', 'completed', 'failed', 'skipped']);
export type KnowledgeAnalyzeStepStatus = z.infer<typeof KnowledgeAnalyzeStepStatusSchema>;

export const KnowledgeAnalyzeStepSchema = z.object({
  id: z.string().min(1).max(80),
  label: z.string().min(1).max(200),
  status: KnowledgeAnalyzeStepStatusSchema,
});
export type KnowledgeAnalyzeStep = z.infer<typeof KnowledgeAnalyzeStepSchema>;

export const KnowledgeAnalyzeSourceStatusSchema = z.enum(['queued', 'running', 'analyzed', 'failed', 'skipped']);
export type KnowledgeAnalyzeSourceStatus = z.infer<typeof KnowledgeAnalyzeSourceStatusSchema>;

export const KnowledgeAnalyzeSourceSchema = z.object({
  id: z.string().uuid(),
  kind: z.enum(['vcs', 'local']),
  label: z.string().min(1).max(300),
  status: KnowledgeAnalyzeSourceStatusSchema,
  percent: z.number().int().min(0).max(100),
  message: z.string().max(500).optional(),
});
export type KnowledgeAnalyzeSource = z.infer<typeof KnowledgeAnalyzeSourceSchema>;

export const KnowledgeAnalyzeRunStatusSchema = z.enum(['queued', 'running', 'completed', 'failed', 'cancelled']);
export type KnowledgeAnalyzeRunStatus = z.infer<typeof KnowledgeAnalyzeRunStatusSchema>;

export const KnowledgeAnalyzeRunSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  status: KnowledgeAnalyzeRunStatusSchema,
  overallPercent: z.number().int().min(0).max(100),
  steps: z.array(KnowledgeAnalyzeStepSchema),
  sources: z.array(KnowledgeAnalyzeSourceSchema),
  errorMessage: z.string().max(500).nullable().optional(),
  startedAt: z.string(),
  completedAt: z.string().nullable(),
});
export type KnowledgeAnalyzeRun = z.infer<typeof KnowledgeAnalyzeRunSchema>;

export const StartKnowledgeAnalyzeResponseSchema = z.object({
  run: KnowledgeAnalyzeRunSchema,
});
export type StartKnowledgeAnalyzeResponse = z.infer<typeof StartKnowledgeAnalyzeResponseSchema>;

export const GetKnowledgeAnalyzeRunResponseSchema = z.object({
  run: KnowledgeAnalyzeRunSchema.nullable(),
});
export type GetKnowledgeAnalyzeRunResponse = z.infer<typeof GetKnowledgeAnalyzeRunResponseSchema>;

/** Structured LLM output for Knowledge Analyze (advisory only). */
export const KnowledgeAiProposedFlowSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(400).optional().default(''),
});
export type KnowledgeAiProposedFlow = z.infer<typeof KnowledgeAiProposedFlowSchema>;

export const KnowledgeAiProposedModuleSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(400).optional().default(''),
  flows: z.array(KnowledgeAiProposedFlowSchema).max(12).optional().default([]),
});
export type KnowledgeAiProposedModule = z.infer<typeof KnowledgeAiProposedModuleSchema>;

export const KnowledgeAiProposedDomainSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(400).optional().default(''),
  modules: z.array(KnowledgeAiProposedModuleSchema).max(12).optional().default([]),
});
export type KnowledgeAiProposedDomain = z.infer<typeof KnowledgeAiProposedDomainSchema>;

export const KnowledgeAiLlmResultSchema = z.object({
  applicationSummary: z.string().max(800).optional().default(''),
  primaryLanguage: z.string().max(80).optional().nullable().default(null),
  framework: z.string().max(120).optional().nullable().default(null),
  databases: z.array(z.string().max(80)).max(8).optional().default([]),
  proposedDomains: z.array(KnowledgeAiProposedDomainSchema).max(12).optional().default([]),
  proposedCapabilities: z.array(z.string().max(160)).max(20).optional().default([]),
  evidenceRefs: z.array(z.string().max(300)).max(40).optional().default([]),
});
export type KnowledgeAiLlmResult = z.infer<typeof KnowledgeAiLlmResultSchema>;

export const KnowledgeAiAnalysisStatusSchema = z.enum(['proposed', 'partial', 'failed', 'skipped', 'applied']);
export type KnowledgeAiAnalysisStatus = z.infer<typeof KnowledgeAiAnalysisStatusSchema>;

export const KnowledgeAiAnalysisSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  analyzeRunId: z.string().uuid().nullable(),
  status: KnowledgeAiAnalysisStatusSchema,
  provider: z.string().nullable(),
  model: z.string().nullable(),
  skipReason: z.string().max(500).nullable().optional(),
  applicationSummary: z.string().nullable(),
  primaryLanguage: z.string().nullable(),
  framework: z.string().nullable(),
  databases: z.array(z.string()),
  proposedDomains: z.array(KnowledgeAiProposedDomainSchema),
  proposedCapabilities: z.array(z.string()),
  evidenceRefs: z.array(z.string()),
  createdAt: z.string(),
});
export type KnowledgeAiAnalysis = z.infer<typeof KnowledgeAiAnalysisSchema>;

export const KnowledgeAiConfirmRequestSchema = z.object({
  /** When omitted, confirms all domains from the latest proposed analysis. */
  domainNames: z.array(z.string().min(1).max(120)).max(12).optional(),
});
export type KnowledgeAiConfirmRequest = z.infer<typeof KnowledgeAiConfirmRequestSchema>;

export const KnowledgeAiConfirmResponseSchema = z.object({
  createdDomains: z.number().int().nonnegative(),
  createdModules: z.number().int().nonnegative(),
  createdFlows: z.number().int().nonnegative(),
});
export type KnowledgeAiConfirmResponse = z.infer<typeof KnowledgeAiConfirmResponseSchema>;
